import React, { useState, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import FormInput from "../../questionnaire/components/FormInput";
import FormSelect from "../../questionnaire/components/FormSelect";
import AppButton from "../../questionnaire/components/AppButton";
import CheckinLayout from "../../questionnaire/components/CheckinLayout";
import { useAppointmentStepPath } from "../../questionnaire/routes/stepConfig";
import { useDemographics } from "../../questionnaire/contexts/DemographicsContext";
import { useMasterData } from "../../questionnaire/contexts/MasterDataContext";
import { useWorkflow } from "../../questionnaire/contexts/WorkflowContext";
import { useLoader } from "../../questionnaire/contexts/LoaderContext";
import { mapValueToMasterValue } from "../../questionnaire/utils/masterData";
import { months, usStates, countries } from "../../questionnaire/questionnaire_constants";
import { Alert } from "../../components/shared/Alert";

const relationshipPlaceholder = { label: "Select Option", value: "" };

/** MM/DD/YYYY string → [day, month, year] for guarantor_dob_* inputs (same semantics as PersonalInformation parseDob). */
function parseGuarantorDob(dob?: string | null): [string, string, string] {
  if (!dob) {
    return ["", "", ""];
  }
  const parts = dob.split("/");
  if (parts.length !== 3) {
    return ["", "", ""];
  }
  const [rawMonth, rawDay, year] = parts;
  const day = rawDay || "";
  const month = rawMonth ? String(parseInt(rawMonth, 10)) : "";
  return [day, month, year || ""];
}

const EmergencyAndBilling: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { demographics, loading, error, updateDemographics } = useDemographics();
  const { data: masterData } = useMasterData();
  const { getNextStepInSequence, getPreviousStep, isAllStepsCompleted, error: workflowError, loading: workflowLoading, refreshWorkflow } = useWorkflow();
  const { show, hide, withLoader } = useLoader();

  const addressPath = useAppointmentStepPath("address-and-identity");
  const carePath = useAppointmentStepPath("care-and-insurance");

  React.useEffect(() => {
    // Hide the loader as soon as an error is reported — otherwise the
    // overlay covers the error Alert and the user sees an infinite spinner
    // (e.g. PAST_APPOINTMENT keeps `loading=false` but `demographics=null`).
    const hasError = !!(error || workflowError);
    const initialLoading = (workflowLoading || loading || !demographics || !masterData) && !hasError;
    if (initialLoading) show("Loading checkin...");
    else hide();
    return () => hide();
  }, [workflowLoading, loading, demographics, masterData, error, workflowError, show, hide]);

      const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const inFlightSubmitRef = useRef(false);
  const [billingSameAsPatient, setBillingSameAsPatient] = useState<"yes" | "no">("yes");

  // Store initial state for change detection
  const [initialState, setInitialState] = useState<{
    emergencyContact: {
      name: string;
      mobilePhone: string;
      homePhone: string;
      relationship: string;
    };
    billingSameAsPatient: "yes" | "no";
    billingInfo: {
      guarantorFirstName: string;
      guarantorLastName: string;
      guarantorDateOfBirth: string;
      guarantorRelationshipToPatient: string;
      guarantorAddress1: string;
      guarantorAddress2: string;
      guarantorCity: string;
      guarantorState: string;
      guarantorZip: string;
      guarantorCountryCode: string;
      guarantorEmail: string;
      guarantorPhone: string;
    } | null;
  } | null>(null);

  React.useEffect(() => {
    if (demographics?.billingInfo?.guarantorAddressSameAsPatient === false) {
      setBillingSameAsPatient("no");
    }
  }, [demographics]);

  // Initialize state for change detection
  React.useEffect(() => {
    const emergency = demographics?.emergencyContact;
    const billingInfo = demographics?.billingInfo;
    
    if (demographics) {
      setInitialState({
        emergencyContact: {
          name: emergency?.name || "",
          mobilePhone: emergency?.mobilePhone || "",
          homePhone: emergency?.homePhone || "",
          relationship: emergency?.relationship || "",
        },
        billingSameAsPatient: billingInfo?.guarantorAddressSameAsPatient === false ? "no" : "yes",
        billingInfo: billingInfo?.guarantorAddressSameAsPatient === false ? {
          guarantorFirstName: billingInfo?.guarantorFirstName || "",
          guarantorLastName: billingInfo?.guarantorLastName || "",
          guarantorDateOfBirth: billingInfo?.guarantorDateOfBirth || "",
          guarantorRelationshipToPatient: String(billingInfo?.guarantorRelationshipToPatient || ""),
          guarantorAddress1: billingInfo?.guarantorAddress1 || "",
          guarantorAddress2: billingInfo?.guarantorAddress2 || "",
          guarantorCity: billingInfo?.guarantorCity || "",
          guarantorState: billingInfo?.guarantorState || "",
          guarantorZip: billingInfo?.guarantorZip || "",
          guarantorCountryCode: billingInfo?.guarantorCountryCode3166 || "",
          guarantorEmail: billingInfo?.guarantorEmail || "",
          guarantorPhone: billingInfo?.guarantorPhone || "",
        } : null,
      });
    }
  }, [demographics]);

  const handleBillingToggle = (value: "yes" | "no") => {
    setBillingSameAsPatient(value);
  };

  // Helper function to check if there are any changes
  const hasChanges = (formData: FormData, currentBillingSameAsPatient: "yes" | "no"): boolean => {
    if (!initialState) return true; // If no initial state, assume there are changes

    // Check emergency contact changes
    const currentEmergencyName = (formData.get("emergency_full_name") as string) || "";
    const currentEmergencyPhone = (formData.get("emergency_phone") as string) || "";
    const currentEmergencyHome = (formData.get("emergency_home") as string) || "";
    const currentEmergencyRelationship = (formData.get("emergency_relationship") as string) || "";

    if (
      currentEmergencyName !== initialState.emergencyContact.name ||
      currentEmergencyPhone !== initialState.emergencyContact.mobilePhone ||
      currentEmergencyHome !== initialState.emergencyContact.homePhone ||
      currentEmergencyRelationship !== initialState.emergencyContact.relationship
    ) {
      return true;
    }

    // Check billing same as patient toggle
    if (currentBillingSameAsPatient !== initialState.billingSameAsPatient) {
      return true;
    }

    // If billing is same as patient, no need to check billing info
    if (currentBillingSameAsPatient === "yes") {
      return false;
    }

    // Check billing info changes
    const currentBillingInfo = {
      guarantorFirstName: (formData.get("guarantor_first_name") as string) || "",
      guarantorLastName: (formData.get("guarantor_last_name") as string) || "",
      guarantorDateOfBirth: (() => {
        const day = (formData.get("guarantor_dob_day") as string) || "";
        const month = (formData.get("guarantor_dob_month") as string) || "";
        const year = (formData.get("guarantor_dob_year") as string) || "";
        return day && month && year ? `${month}/${day}/${year}` : "";
      })(),
      guarantorRelationshipToPatient: (formData.get("guarantor_relationship") as string) || "",
      guarantorAddress1: (formData.get("guarantor_address_line1") as string) || "",
      guarantorAddress2: (formData.get("guarantor_address_line2") as string) || "",
      guarantorCity: (formData.get("guarantor_city") as string) || "",
      guarantorState: (formData.get("guarantor_state") as string) || "",
      guarantorZip: (formData.get("guarantor_zip") as string) || "",
      guarantorCountryCode3166: (formData.get("guarantor_country") as string) || "",
      guarantorEmail: (formData.get("guarantor_email") as string) || "",
      guarantorPhone: (formData.get("guarantor_phone") as string) || "",
    };

    const initialBillingInfo = initialState.billingInfo;
    if (!initialBillingInfo) {
      // If initial state had billing same as patient, but now it's different, there are changes
      return true;
    }

    if (
      currentBillingInfo.guarantorFirstName !== initialBillingInfo.guarantorFirstName ||
      currentBillingInfo.guarantorLastName !== initialBillingInfo.guarantorLastName ||
      currentBillingInfo.guarantorDateOfBirth !== initialBillingInfo.guarantorDateOfBirth ||
      currentBillingInfo.guarantorRelationshipToPatient !== initialBillingInfo.guarantorRelationshipToPatient ||
      currentBillingInfo.guarantorAddress1 !== initialBillingInfo.guarantorAddress1 ||
      currentBillingInfo.guarantorAddress2 !== initialBillingInfo.guarantorAddress2 ||
      currentBillingInfo.guarantorCity !== initialBillingInfo.guarantorCity ||
      currentBillingInfo.guarantorState !== initialBillingInfo.guarantorState ||
      currentBillingInfo.guarantorZip !== initialBillingInfo.guarantorZip ||
      currentBillingInfo.guarantorCountryCode3166 !== initialBillingInfo.guarantorCountryCode ||
      currentBillingInfo.guarantorEmail !== initialBillingInfo.guarantorEmail ||
      currentBillingInfo.guarantorPhone !== initialBillingInfo.guarantorPhone
    ) {
      return true;
    }

    return false;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inFlightSubmitRef.current) return;
    inFlightSubmitRef.current = true;
    try {

    const formData = new FormData(e.currentTarget);

    // Check if there are any changes before calling the API
    if (!hasChanges(formData, billingSameAsPatient)) {
      // No changes detected, just navigate to next page
      navigate(carePath);
      return;
    }

    const emergencyContact = {
      name: (formData.get("emergency_full_name") as string) || undefined,
      mobilePhone: (formData.get("emergency_phone") as string) || undefined,
      homePhone: (formData.get("emergency_home") as string) || undefined,
      relationship: (formData.get("emergency_relationship") as string) || undefined,
      contactPreference: "MOBILEPHONE",
    };

    const payload: any = {
      emergencyContact,
    };

    if (billingSameAsPatient === "no") {
      const guarantorDobDay = (formData.get("guarantor_dob_day") as string) || "";
      const guarantorDobMonth = (formData.get("guarantor_dob_month") as string) || "";
      const guarantorDobYear = (formData.get("guarantor_dob_year") as string) || "";

      payload.billingInfo = {
        guarantorFirstName: (formData.get("guarantor_first_name") as string) || undefined,
        guarantorLastName: (formData.get("guarantor_last_name") as string) || undefined,
        guarantorDateOfBirth:
          guarantorDobDay && guarantorDobMonth && guarantorDobYear
            ? `${guarantorDobMonth}/${guarantorDobDay}/${guarantorDobYear}`
            : undefined,
        guarantorRelationshipToPatient: (formData.get("guarantor_relationship") as string) || undefined,
        guarantorAddress1: (formData.get("guarantor_address_line1") as string) || undefined,
        guarantorAddress2: (formData.get("guarantor_address_line2") as string) || undefined,
        guarantorCity: (formData.get("guarantor_city") as string) || undefined,
        guarantorState: (formData.get("guarantor_state") as string) || undefined,
        guarantorZip: (formData.get("guarantor_zip") as string) || undefined,
        guarantorCountryCode: (formData.get("guarantor_country") as string) || undefined,
        guarantorEmail: (formData.get("guarantor_email") as string) || undefined,
        guarantorPhone: (formData.get("guarantor_phone") as string) || undefined,
        guarantorAddressSameAsPatient: false,
      };
} else {
      payload.billingInfo = {
        guarantorAddressSameAsPatient: true,
      };
    }

    setSubmitting(true);
    setLocalError(null);
    try {
      await withLoader("Saving...", async () => {
        await updateDemographics(payload);
        // Refetch workflow so the progress bar reflects backend truth
        // (step statuses, newly-completed steps) before we navigate.
        await refreshWorkflow();
        if (isAllStepsCompleted()) {
          navigate(`/appointments/${appointmentId}/complete`);
        } else {
          navigate(carePath);
        }
      });
    } catch (err) {
      setLocalError("Unable to save your information. Please try again.");
    } finally {
      setSubmitting(false);
    }
    } finally {
      inFlightSubmitRef.current = false;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSaveAndExit = async () => {
    const formData = new FormData(document.forms[0] as HTMLFormElement);

    // Check if there are any changes before calling the API
    // For Save & Exit, we only check emergency contact changes (simplified)
    if (initialState) {
      const currentEmergencyName = (document.querySelector('[name="emergency_full_name"]') as HTMLInputElement)?.value || "";
      const currentEmergencyPhone = (document.querySelector('[name="emergency_phone"]') as HTMLInputElement)?.value || "";
      const currentEmergencyRelationship = (document.querySelector('[name="emergency_relationship"]') as HTMLInputElement)?.value || "";

      const hasEmergencyChanges = 
        currentEmergencyName !== initialState.emergencyContact.name ||
        currentEmergencyPhone !== initialState.emergencyContact.mobilePhone ||
        currentEmergencyRelationship !== initialState.emergencyContact.relationship;

      if (!hasEmergencyChanges) {
        // No changes detected, navigate to next step in sequence or completion
        if (isAllStepsCompleted()) {
          navigate(`/appointments/${appointmentId}/complete`);
        } else {
          const nextStep = getNextStepInSequence(location.pathname);
          if (nextStep) {
            navigate(nextStep.stepUrl);
          } else {
            navigate(carePath.replace("care-and-insurance", "payments"));
          }
        }
        return;
      }
    }

    const emergencyContact = {
      name: (document.querySelector('[name="emergency_full_name"]') as HTMLInputElement)?.value || undefined,
      mobilePhone: (document.querySelector('[name="emergency_phone"]') as HTMLInputElement)?.value || undefined,
      relationship: (document.querySelector('[name="emergency_relationship"]') as HTMLInputElement)?.value || undefined,
    };

    const payload = {
      emergencyContact,
    };

    setSubmitting(true);
    setLocalError(null);
    try {
      await updateDemographics(payload);
      // Navigate to next step in sequence or completion
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          navigate(carePath.replace("care-and-insurance", "payments"));
        }
      }
    } catch (err) {
      setLocalError("Unable to save your information. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const emergency = demographics?.emergencyContact;
  const billingInfo = demographics?.billingInfo;

  const emergencyRelationshipDefault = mapValueToMasterValue(
    emergency?.relationship,
    masterData?.emergencyContactRelationship,
    "code"
  );
  const guarantorRelationshipDefault = mapValueToMasterValue(
    billingInfo?.guarantorRelationshipToPatient,
    masterData?.relationshipToPatient,
    "id"
  );

  const guarantorDob = parseGuarantorDob(billingInfo?.guarantorDateOfBirth || null);

  return (
    <CheckinLayout
      title="Emergency & Billing Contacts"
      subtitle="Who should we contact in case of emergency?"
    >
        {(error || localError) && (
          <div className="w-full max-w-[1200px] mb-2">
            <Alert
              type="error"
              onClose={() => {
                setLocalError(null);
              }}
            >
              {error || localError}
            </Alert>
          </div>
        )}
        <form
          className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[1200px] flex flex-col gap-8 mt-4"
          onSubmit={handleSubmit}
        >
          <section className="flex flex-col gap-4">
            <h2 className="text-xl font-semibold text-[#01545D]">Emergency Contact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                label="Full Name"
                name="emergency_full_name"
                placeholder="Value"
                required
                defaultValue={emergency?.name || ""}
              />
                <FormSelect
                   label="Relationship"
                   name="emergency_relationship"
                   key={`emergency-relationship-${masterData?.emergencyContactRelationship?.length || 0}-${emergencyRelationshipDefault}`}
                   options={[
                     relationshipPlaceholder,
                     ...(masterData?.emergencyContactRelationship?.map((rel) => {
                       return {
                         label: rel.name,
                         value: rel.code
                       };
                     }) || [])
                   ]}
                  required
                  defaultValue={emergencyRelationshipDefault}
                />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                label="Phone Number"
                name="emergency_phone"
                type="tel"
                placeholder="Value"
                required
                defaultValue={emergency?.mobilePhone || ""}
              />
              <FormInput
                label="Home Number"
                name="emergency_home"
                type="tel"
                placeholder="Value"
                defaultValue={emergency?.homePhone || ""}
              />
            </div>
          </section>

          <div className="border-t border-[#DCE1E8]" />

          <section className="flex flex-col gap-4">
            <h2 className="text-xl font-semibold text-[#01545D]">Billing / Guarantor Information</h2>
            <div className="flex flex-col gap-3">
              <div className="font-semibold text-[#1C1C1C] text-base">
                Is billing contact same as patient? <span className="text-red-500">*</span>
              </div>
              <div className="flex gap-4 max-w-md">
                <button
                  type="button"
                  onClick={() => handleBillingToggle("yes")}
                  className={
                    "flex-1 py-3 px-5 rounded-md border text-base font-medium " +
                    (billingSameAsPatient === "yes"
                      ? "border-[#01545D] bg-[#01545D] text-white"
                      : "border-[#DCE1E8] bg-white text-[#1C1C1C]")
                  }
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => handleBillingToggle("no")}
                  className={
                    "flex-1 py-3 px-5 rounded-md border text-base font-medium " +
                    (billingSameAsPatient === "no"
                      ? "border-[#01545D] bg-[#01545D] text-white"
                      : "border-[#DCE1E8] bg-white text-[#1C1C1C]")
                  }
                >
                  No
                </button>
              </div>
            </div>

            {billingSameAsPatient === "no" && (
              <div className="flex flex-col gap-6 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormInput
                    label="Guarantor First Name"
                    name="guarantor_first_name"
                    placeholder="Value"
                    required
                    defaultValue={billingInfo?.guarantorFirstName || ""}
                  />
                  <FormInput
                    label="Guarantor Last Name"
                    name="guarantor_last_name"
                    placeholder="Value"
                    required
                    defaultValue={billingInfo?.guarantorLastName || ""}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <label className="font-semibold text-[#1C1C1C] text-base">
                      Guarantor Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <input
                          type="number"
                          name="guarantor_dob_day"
                          placeholder="DD"
                          min={1}
                          max={31}
                          required
                          defaultValue={guarantorDob[0] || ""}
                          className="w-full rounded-md border border-[#DCE1E8] py-3 px-3 text-base text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                        />
                      </div>
                      <div className="flex-1">
                        <FormSelect
                          label=""
                          name="guarantor_dob_month"
                          options={months}
                          required
                          className="w-full"
                          defaultValue={guarantorDob[1] || ""}
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="number"
                          name="guarantor_dob_year"
                          placeholder="YYYY"
                          min={1900}
                          max={new Date().getFullYear()}
                          required
                          defaultValue={guarantorDob[2] || ""}
                          className="w-full rounded-md border border-[#DCE1E8] py-3 px-3 text-base text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]"
                        />
                      </div>
                    </div>
                  </div>
                     <FormSelect
                       label="Relationship to Patient"
                       name="guarantor_relationship"
                       options={[
                         relationshipPlaceholder,
                         ...(masterData?.relationshipToPatient?.map((rel) => ({
                           label: rel.name,
                           value: rel.id,
                         })) || []),
                       ]}
                       required
                       defaultValue={guarantorRelationshipDefault}
                     />
                </div>

                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 gap-4">
                    <FormInput
                      label="Guarantor Address"
                      name="guarantor_address_line1"
                      placeholder="Address Line 1"
                      required
                      defaultValue={billingInfo?.guarantorAddress1 || ""}
                    />
                    <FormInput
                      label=""
                      name="guarantor_address_line2"
                      placeholder="Address Line 2 (Optional)"
                      defaultValue={billingInfo?.guarantorAddress2 || ""}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormSelect
                      label="Country"
                      name="guarantor_country"
                      options={countries}
                      required
                      defaultValue={billingInfo?.guarantorCountryCode3166 || ""}
                    />
                    <FormSelect
                      label="State"
                      name="guarantor_state"
                      options={usStates}
                      required
                      defaultValue={billingInfo?.guarantorState || ""}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormInput
                      label="City"
                      name="guarantor_city"
                      placeholder="Value"
                      required
                      defaultValue={billingInfo?.guarantorCity || ""}
                    />
                    <FormInput
                      label="Zip Code"
                      name="guarantor_zip"
                      placeholder="Value"
                      required
                      defaultValue={billingInfo?.guarantorZip || ""}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormInput
                    label="Guarantor Email"
                    name="guarantor_email"
                    type="email"
                    placeholder="Value"
                    required
                    defaultValue={billingInfo?.guarantorEmail || ""}
                  />
                  <FormInput
                    label="Guarantor Phone"
                    name="guarantor_phone"
                    type="tel"
                    placeholder="Value"
                    required
                    defaultValue={billingInfo?.guarantorPhone || ""}
                  />
                </div>
              </div>
            )}
          </section>

          <div className="flex gap-5 w-full mt-8">
            <AppButton
              variant="secondary"
              type="button"
              onClick={() => {
                const previousStep = getPreviousStep(location.pathname);
                if (previousStep) {
                  navigate(previousStep.stepUrl);
                } else {
                  navigate(addressPath);
                }
              }}
              disabled={!!workflowError || submitting || loading}
              className="flex-1"
            >
              Back
            </AppButton>
            {/* <AppButton
              variant="secondary"
              type="button"
              onClick={handleSaveAndExit}
              disabled={submitting || loading}
              className="flex-1"
            >
              {submitting ? "Saving..." : "Save & Exit"}
            </AppButton> */}
            <AppButton type="submit" disabled={!!workflowError || submitting || loading} className="flex-1">
              {submitting ? "Saving..." : "Continue"}
            </AppButton>
          </div>
        </form>
    </CheckinLayout>
  );
};

export default EmergencyAndBilling;
